import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useI18n } from "../../i18n/I18nContext";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { Logo } from "../../components/Logo";
import { LanguageToggle } from "../../components/LanguageToggle";
import { LayoutGrid, CalendarDays, Users, Camera, UserCog, CreditCard, Store,
  Boxes, Award, MessageSquareWarning, BarChart3, ShieldCheck, Plug, ScrollText,
  Settings, Menu, X, LogOut, Home, MonitorSmartphone, Building2, Fingerprint, AlertTriangle, IdCard } from "lucide-react";

const ITEMS = [
  ["/admin", "overview", LayoutGrid],
  ["/admin/content", "content", MonitorSmartphone],
  ["/admin/bookings", "bookings", CalendarDays],
  ["/admin/customers", "customers", Users],
  ["/admin/services", "services", Camera],
  ["/admin/branches", "branches", Building2],
  ["/admin/staff", "staff", UserCog],
  ["/admin/attendance", "attendance", Fingerprint],
  ["/admin/payments", "payments", CreditCard],
  ["/admin/cashier", "cashier", Store],
  ["/admin/inventory", "inventory", Boxes],
  ["/admin/loyalty", "loyalty", Award],
  ["/admin/complaints", "complaints", MessageSquareWarning],
  ["/admin/reports", "reports", BarChart3],
  ["/admin/roles", "roles", ShieldCheck],
  ["/admin/integrations", "integrations", Plug],
  ["/admin/audit", "audit", ScrollText],
  ["/admin/settings", "settings", Settings],
];

export function AdminLayout({ children }) {
  const { t, isAr } = useI18n();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const isMgr = ["super_admin", "branch_manager"].includes(user?.role);
  useEffect(() => { if (isMgr) api.get("/admin/alerts").then((r) => setAlerts(r.data)).catch(() => {}); }, [isMgr]);

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 z-40 w-64 shrink-0 border-e border-border bg-white transition-transform lg:static lg:!translate-x-0 ${open ? "translate-x-0" : "ltr:-translate-x-full rtl:translate-x-full"}`}>
        <div className="flex items-center justify-between border-b border-border px-4 py-4">
          <Logo height={32} />
          <button onClick={() => setOpen(false)} className="lg:hidden"><X className="h-5 w-5" /></button>
        </div>
        <nav className="flex h-[calc(100vh-64px)] flex-col overflow-y-auto p-3 no-scrollbar">
          {ITEMS.map(([to, key, Icon]) => (
            <NavLink key={to} to={to} end={to === "/admin"} onClick={() => setOpen(false)} data-testid={`admin-nav-${key}`}
              className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-primary text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              <Icon className="h-4 w-4" /> {t(`admin.${key}`)}
            </NavLink>
          ))}
          <div className="mt-auto space-y-1 border-t border-border pt-3">
            <button onClick={() => nav("/")} className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-slate-600 hover:bg-slate-100"><Home className="h-4 w-4" /> {t("nav.home")}</button>
            <button onClick={() => { logout(); nav("/"); }} data-testid="admin-logout" className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-[hsl(var(--brand-red))] hover:bg-red-50"><LogOut className="h-4 w-4" /> {t("nav.logout")}</button>
          </div>
        </nav>
      </aside>
      {open && <div className="fixed inset-0 z-30 bg-black/30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex flex-1 flex-col">
        <header className="flex items-center justify-between border-b border-border bg-white px-4 py-3">
          <button data-testid="mobile-menu" onClick={() => setOpen(true)} className="lg:hidden"><Menu className="h-6 w-6" /></button>
          <div className="flex-1" />
          <div className="flex items-center gap-3">
            <button onClick={() => nav("/staff")} data-testid="header-portal" className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-medium text-slate-600 hover:border-primary hover:text-primary"><IdCard className="h-4 w-4" /> {t("admin.portal")}</button>
            <LanguageToggle />
            <span className="hidden text-sm text-slate-500 sm:block">{user?.name} · <b className="text-primary">{user?.role}</b></span>
          </div>
        </header>
        {isMgr && alerts.length > 0 && (
          <div className="border-b border-amber-200 bg-amber-50 px-4 py-2.5" data-testid="alerts-banner">
            <div className="flex flex-wrap items-center gap-2 text-sm text-amber-800">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <b>{alerts.length}</b> {t("admin.alerts")}:
              <span className="truncate">{alerts.slice(0, 2).map((a) => (isAr ? a.title_ar : a.title_en)).join(" · ")}</span>
            </div>
          </div>
        )}
        <main className="flex-1 p-4 sm:p-6">{children}</main>
      </div>
    </div>
  );
}
